// @spec VUL-STR-033
// Pending acceptance approvals page for vulnerability acceptance workflow
'use client';

import { useAuth } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle, RefreshCw, Shield } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePendingAcceptances } from '@/features/vulnerabilities/api/use-acceptance-approval';
import { PendingAcceptancesList } from '@/features/vulnerabilities/components/PendingAcceptancesList';
import type { VulnerabilitySeverity } from '@/features/vulnerabilities/types';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/vulnerabilities/approvals/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.pendingAcceptanceApprovals'),
  }),
  component: PendingAcceptanceApprovalsPage,
});

/**
 * Page for managing pending vulnerability acceptance approvals.
 *
 * VUL-STR-033: Approve Risk Acceptance (Four-Eyes)
 * Shows a list of acceptance requests awaiting approval with filtering capabilities.
 */
function PendingAcceptanceApprovalsPage() {
  const navigate = useNavigate();
  const { userId } = useAuth();

  // Filter state
  const [severityFilter, setSeverityFilter] = React.useState<VulnerabilitySeverity | null>(null);
  const [page, setPage] = React.useState(1);

  // Selection state for bulk mode (future enhancement)
  const [bulkMode, setBulkMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  // Fetch pending acceptances
  const {
    data: acceptancesData,
    isLoading,
    refetch,
  } = usePendingAcceptances({
    page,
    pageSize: 20,
    severity: severityFilter ? [severityFilter] : null,
  });

  const handleRefresh = () => {
    void refetch();
    setSelectedIds([]);
  };

  const handleSelectAcceptance = (vulnerabilityId: string) => {
    void navigate({
      to: '/vulnerabilities/$vulnerabilityId',
      params: { vulnerabilityId },
    });
  };

  const handleToggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedIds([]);
  };

  const handleSeverityFilterChange = (severity: VulnerabilitySeverity | null) => {
    setSeverityFilter(severity);
    setPage(1);
    setSelectedIds([]);
  };

  const pendingCount = acceptancesData?.pagination.totalItems ?? 0;
  const items = acceptancesData?.items ?? [];
  const totalPages = acceptancesData?.pagination.totalPages ?? 1;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => void navigate({ to: '/vulnerabilities' })}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Vulnerabilities
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Page Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Pending Acceptance Approvals</h1>
            {pendingCount > 0 && (
              <Badge variant="secondary" className="text-lg">
                {pendingCount}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Review and approve vulnerability risk acceptance requests. You cannot approve requests
            you submitted (four-eyes principle).
          </p>
        </div>

        {/* Bulk Actions Toolbar */}
        <div className="flex items-center justify-between rounded-lg border bg-card p-4">
          <div className="flex items-center gap-4">
            <Button
              variant={bulkMode ? 'secondary' : 'outline'}
              size="sm"
              onClick={handleToggleBulkMode}
            >
              {bulkMode ? 'Exit Bulk Mode' : 'Enable Bulk Mode'}
            </Button>
            {bulkMode && selectedIds.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          {bulkMode && (
            <Button disabled={selectedIds.length === 0}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Bulk Approve ({selectedIds.length})
            </Button>
          )}
        </div>

        {/* Acceptances List */}
        <PendingAcceptancesList
          items={items}
          isLoading={isLoading}
          onSelectAcceptance={handleSelectAcceptance}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          bulkMode={bulkMode}
          onRefresh={handleRefresh}
          severityFilter={severityFilter}
          onSeverityFilterChange={handleSeverityFilterChange}
          {...(userId ? { currentUserId: userId } : {})}
        />

        {/* Pagination */}
        {acceptancesData && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => {
                setPage((p) => p - 1);
              }}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => {
                setPage((p) => p + 1);
              }}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
